import { NA, fmt, crore, isNum, signClass, badgeClass, moneyParts } from "../../lib/format.js";
import Icon from "./Icon.jsx";

export function NotAvailable({ checkAt, note }) {
  return <span className="na">{note ?? (checkAt ? `${NA} — check at ${checkAt}` : NA)}</span>;
}

/**
 * A rupee figure. The symbol and the paise are set smaller and lighter than
 * the rupees so the digits carrying the decision are what the eye lands on,
 * and `size` picks a step on the shared numeric scale rather than leaving
 * every call site to invent its own font-size.
 */
export function Money({ value, digits, size, tone, dash = "—" }) {
  const parts = moneyParts(value, digits);
  if (!parts) return <span className="neu">{dash}</span>;
  return (
    <span className={`money${size ? ` money-${size}` : ""}${tone ? ` ${tone}` : ""}`}>
      {parts.sign}
      <span className="money-sym">₹</span>
      <span className="money-int">{parts.int}</span>
      {parts.dec && <span className="money-dec">.{parts.dec}</span>}
    </span>
  );
}

/**
 * Signed movement. Direction is carried by an arrow as well as colour, because
 * red/green alone is invisible to the commonest form of colour blindness.
 */
export function Delta({ value, digits = 1, suffix = "%", size, dash = "—" }) {
  if (!isNum(value)) return <span className="neu">{dash}</span>;
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  return (
    <span className={`delta ${dir}${size ? ` delta-${size}` : ""}`}>
      {/* No glyph when flat: a dash butted against a figure reads as a minus. */}
      {dir !== "flat" && <span className="delta-arrow" aria-hidden="true">{dir === "up" ? "▲" : "▼"}</span>}
      <span>{value > 0 ? "+" : value < 0 ? "−" : ""}{fmt(Math.abs(value), digits)}{suffix}</span>
    </span>
  );
}

/**
 * A labelled figure. Label above, value below, value dominant — the pairing
 * that used to be built ad hoc in each tab with the label at the same weight
 * as the number it described.
 */
export function Figure({ label, value, sub, tone, size = "md", hint }) {
  return (
    <div className={`figure figure-${size}${tone ? ` ${tone}` : ""}`}>
      <div className="figure-label">
        {label}
        {hint && <span className="figure-hint" title={hint}><Icon name="info" /></span>}
      </div>
      <div className="figure-value">{value}</div>
      {sub && <div className="figure-sub">{sub}</div>}
    </div>
  );
}

/** A plain number, or an em dash when absent. */
export function Num({ value, digits = 2, prefix = "", suffix = "", dash = "—" }) {
  const s = fmt(value, digits);
  return s === null ? <span className="neu">{dash}</span> : <>{prefix}{s}{suffix}</>;
}

/** A {value, source, available, note} metric straight off the API. */
export function Metric({ metric, digits, prefix = "", suffix = "", checkAt = "Screener.in", money = false }) {
  if (!metric?.available) return <NotAvailable note={metric?.note} checkAt={checkAt} />;
  // Undefined digits lets <Money> scale precision to the size of the figure;
  // an explicit value still wins, which is what a whole-rupee face value needs.
  if (money) return <Money value={metric.value} digits={digits} />;
  return <>{prefix}{fmt(metric.value, digits ?? 2)}{suffix}</>;
}

export const sourceOf = (m) => (m?.available ? (m.source ?? "—") : "—");

export function Crore({ value }) {
  const s = crore(value);
  return s === null ? <NotAvailable /> : <>{s}</>;
}

/** Signed percentage, coloured by direction. */
export function Pct({ value, digits = 1, dash = "—" }) {
  const s = fmt(value, digits);
  if (s === null) return <span className="neu">{dash}</span>;
  return <span className={signClass(value)}>{value > 0 ? "+" : ""}{s}%</span>;
}

export function Badge({ word, fallback = "—" }) {
  if (!word) return <span className="badge neu">{fallback}</span>;
  return <span className={`badge ${badgeClass(word)}`}>{word}</span>;
}

/** Numeric score with a proportional bar; reads faster than a bare number. */
export function Score({ value, tone }) {
  if (value === null || value === undefined) return <span className="neu">—</span>;
  const band = tone ?? (value >= 68 ? "green" : value >= 45 ? "amber" : "red");
  return (
    <span className={`score ${band}`}>
      <span className="score-num">{value}</span>
      <span className="score-bar">
        <i style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
      </span>
    </span>
  );
}

export function Card({ tone, title, children, className = "", ...rest }) {
  return (
    <div className={`card ${tone ?? ""} ${className}`} {...rest}>
      {title && <div className="card-title">{title}</div>}
      {children}
    </div>
  );
}

export function InfoRow({ label, children, wide }) {
  return (
    <div className="info-row">
      <span className="il">{label}</span>
      <span
        className="iv"
        style={wide ? { maxWidth: 460, fontWeight: 400, textAlign: "right" } : undefined}
      >
        {children}
      </span>
    </div>
  );
}

export function Tile({ label, value, sub }) {
  return (
    <div className="mc">
      <div className="ml">{label}</div>
      <div className="mv">{value}</div>
      {sub && <div className="ms">{sub}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, tone }) {
  return (
    <div className={`stat ${tone ?? ""}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function Callout({ label, value, extra, children }) {
  return (
    <div className="callout">
      {label}: <strong>{value ?? "INSUFFICIENT DATA"}</strong>
      {extra}
      {children && <span style={{ marginTop: 5, display: "block" }}>{children}</span>}
    </div>
  );
}

// Existing call sites read this as a summary line at the foot of a tab.
export const ScoreBox = Callout;

export function FlagCard({ flag }) {
  return (
    <div className={`flag ${flag.severity === "high" ? "high" : ""}`}>
      <div className="flag-title">
        <Icon name="alert" />
        {flag.title}
      </div>
      <div className="flag-note">{flag.note}</div>
    </div>
  );
}

// The left rule already carries the tone, so there is no icon to choose.
export function Banner({ tone = "info", title, children, meta }) {
  return (
    <div className={`banner ${tone}`} role={tone === "vlow" || tone === "bad" ? "alert" : undefined}>
      <div className="banner-body">
        {title && <strong>{title}</strong>}
        {title && children ? " " : null}
        {children}
        {meta && <div className="banner-meta">{meta}</div>}
      </div>
    </div>
  );
}

export function LoadingState({ title = "Getting your workspace ready", children }) {
  return (
    <div className="loading-state" role="status">
      <span className="loading-indicator" aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

export { isNum };
